import type { Segment } from './segments';

/**
 * Turning a pile of search hits into a report. Pure.
 *
 * The Knowledge Graph answers each segment independently, so the same standard comes back
 * several times with different scores. Deciding which occurrence to keep, how confident to
 * call it, and what order to show them in is a product decision, not a network one — so it
 * lives here and is tested as a function call.
 */

export interface StandardCandidate {
  caseIdentifierUUID: string;
  statementCode: string;
  description: string;
  jurisdiction: string;
  /** The API's semantic similarity, 0–1. */
  score: number;
  /** Which segment's query produced this hit. */
  segmentIndex: number;
  /** Carried verbatim from the API — displaying it is a licence term, not a nicety. */
  attributionStatement?: string;
  license?: string;
}

export type CoverageBand = 'covered' | 'partial' | 'weak';

export interface CoverageThresholds {
  covered: number;
  partial: number;
}

/**
 * Measured against the live Knowledge Graph on 2026-08-19 (Texas, Mathematics, grade 6):
 *
 *   verbatim standard text .............. 0.92
 *   paraphrased, on topic ............... 0.77
 *   real lesson-plan prose .............. 0.70
 *   off-topic (drama in a maths query) .. 0.63
 *   entirely unrelated (biology) ........ 0.57
 *
 * The last number is the one that matters. The endpoint returns its top-N by similarity
 * with no relevance cutoff, so **its floor is ~0.57, not 0** — an unrelated sentence still
 * comes back looking like a result. The first-guess thresholds (0.8 / 0.6) put `partial`
 * *below* that floor, which would have reported a photosynthesis lesson as partly covering
 * a mathematics standard. A tool that invents coverage is worse than no tool.
 */
export const DEFAULT_THRESHOLDS: CoverageThresholds = { covered: 0.85, partial: 0.75 };

/**
 * Below this a match is indistinguishable from the corpus noise floor and is not reported
 * at all — not even as `weak`. `weak` has to mean "we found something loosely related",
 * and 0.6 against this API does not mean that; it means "least-bad of the five rows we
 * always return".
 *
 * Five probes, so a starting point rather than a settled number. Re-measure once real
 * plans have run — see the plan's Deferred to Implementation.
 */
export const MIN_REPORTABLE_SCORE = 0.7;

export interface Finding {
  caseIdentifierUUID: string;
  statementCode: string;
  description: string;
  jurisdiction: string;
  score: number;
  band: CoverageBand;
  /** The segment that earned the match... */
  segmentIndex: number;
  /** ...and its text, so the teacher can judge the match rather than trust it. */
  evidence: string;
  attributionStatement?: string;
  license?: string;
  /** Canonical URL for the standard at its issuing authority. Filled in after matching. */
  sourceUrl?: string;
  /** e.g. "Student Expectation" — the authority's own name for this kind of row. */
  statementType?: string;
  /** e.g. "Texas Education Agency". */
  author?: string;
}

export interface Coverage {
  findings: Finding[];
  counts: Record<CoverageBand, number>;
  attribution: { statement: string; license: string } | null;
}

function bandFor(score: number, thresholds: CoverageThresholds): CoverageBand {
  if (score >= thresholds.covered) return 'covered';
  if (score >= thresholds.partial) return 'partial';
  return 'weak';
}

/** Keep the higher score; on a tie keep the earlier segment, so the output never flickers. */
function beats(a: StandardCandidate, b: StandardCandidate): boolean {
  if (a.score !== b.score) return a.score > b.score;
  return a.segmentIndex < b.segmentIndex;
}

export function buildCoverage(
  segments: readonly Segment[],
  candidates: readonly StandardCandidate[],
  thresholds: CoverageThresholds = DEFAULT_THRESHOLDS,
  minReportable: number = MIN_REPORTABLE_SCORE,
): Coverage {
  const best = new Map<string, StandardCandidate>();
  for (const candidate of candidates) {
    // Dropped before merging, so a noise-floor hit cannot become the "best" occurrence of a
    // standard that matched properly somewhere else in the document.
    if (candidate.score < minReportable) continue;
    const held = best.get(candidate.caseIdentifierUUID);
    if (!held || beats(candidate, held)) best.set(candidate.caseIdentifierUUID, candidate);
  }

  const evidenceFor = (index: number) =>
    segments.find((segment) => segment.index === index)?.text ?? '';

  const findings: Finding[] = [...best.values()]
    .map((candidate) => ({
      caseIdentifierUUID: candidate.caseIdentifierUUID,
      statementCode: candidate.statementCode,
      description: candidate.description,
      jurisdiction: candidate.jurisdiction,
      score: candidate.score,
      band: bandFor(candidate.score, thresholds),
      segmentIndex: candidate.segmentIndex,
      evidence: evidenceFor(candidate.segmentIndex),
      ...(candidate.attributionStatement === undefined
        ? {}
        : { attributionStatement: candidate.attributionStatement }),
      ...(candidate.license === undefined ? {} : { license: candidate.license }),
    }))
    .sort((a, b) => b.score - a.score || a.statementCode.localeCompare(b.statementCode));

  const counts: Record<CoverageBand, number> = { covered: 0, partial: 0, weak: 0 };
  for (const finding of findings) counts[finding.band] += 1;

  const attributed = candidates.find((c) => c.attributionStatement && c.license);

  return {
    findings,
    counts,
    attribution: attributed
      ? { statement: attributed.attributionStatement!, license: attributed.license! }
      : null,
  };
}

/** The best score any one subject managed for the same text. */
export interface SubjectProbe {
  academicSubject: string;
  bestScore: number;
}

export interface SubjectMismatch {
  suggested: string;
  suggestedScore: number;
  currentScore: number;
}

/**
 * How much better another subject has to look before we say so out loud.
 *
 * Measured: the same grade-3 maths text scored 0.94 against Mathematics, 0.72 against
 * Science and 0.68 against English Language Arts. The gap between a right and a wrong
 * subject is large, so the bar can be set well clear of ordinary variation — telling a
 * teacher they picked the wrong subject when they did not is worse than staying quiet.
 */
const MISMATCH_MARGIN = 0.1;

/**
 * Does the plan look like a different subject?
 *
 * Only worth asking when the chosen subject produced nothing convincing. Two conditions,
 * both required: some other subject clears the `partial` bar the current one could not,
 * and it does so by a margin that is not noise.
 */
export function detectSubjectMismatch(
  currentSubject: string,
  currentBestScore: number,
  probes: readonly SubjectProbe[],
  thresholds: CoverageThresholds = DEFAULT_THRESHOLDS,
  margin: number = MISMATCH_MARGIN,
): SubjectMismatch | null {
  const best = [...probes]
    .filter((p) => p.academicSubject !== currentSubject)
    .sort((a, b) => b.bestScore - a.bestScore)[0];

  if (!best) return null;
  if (best.bestScore < thresholds.partial) return null;
  if (best.bestScore - currentBestScore < margin) return null;

  return {
    suggested: best.academicSubject,
    suggestedScore: best.bestScore,
    currentScore: currentBestScore,
  };
}

// --- the reverse reading: the state's checklist ------------------------------------------

/**
 * One entry in the full standard set for a profile, as the corpus lists it. Items whose
 * normalized type is 'Standard' are the leaf expectations a plan can cover; anything else
 * is structure — a knowledge-and-skills statement, an introduction — and serves as a
 * cluster heading rather than a checklist row.
 */
export interface StandardSetItem {
  caseIdentifierUUID: string;
  statementCode: string;
  description: string;
  normalizedStatementType: string;
}

export interface ChecklistItem {
  caseIdentifierUUID: string;
  statementCode: string;
  description: string;
  /** null: nothing in the plan came close enough to report. */
  band: CoverageBand | null;
  bestScore: number | null;
  /** Which of the teacher's objectives touched this standard, if any. */
  matchedSegmentIndexes: number[];
}

export interface ChecklistCluster {
  /** The shared code prefix, e.g. 111.5.b.4 for 111.5.b.4.A…G. */
  code: string;
  /** The structural item's own words, when the set includes one for this code. */
  description: string | null;
  items: ChecklistItem[];
  coveredCount: number;
}

export interface StandardsChecklist {
  clusters: ChecklistCluster[];
  totalCount: number;
  coveredCount: number;
}

/** A leaf's cluster is its code minus the last dot-segment: 111.5.b.4.G → 111.5.b.4. */
function clusterCodeOf(statementCode: string): string {
  const cut = statementCode.lastIndexOf('.');
  return cut > 0 ? statementCode.slice(0, cut) : statementCode;
}

/**
 * The reading in reverse: not "which standards does this plan match" but "of everything
 * the state asks for at this grade, what does the plan touch". Pure.
 *
 * Honesty limit, worth stating: coverage is judged from the candidates the forward search
 * returned — the top matches per objective — not from a fresh search per standard. A
 * standard marked uncovered means nothing in the plan surfaced it as a match, not that a
 * dedicated search for it would also come up empty. For a checklist of what to look at
 * next, that is the right cheap answer; it is not an audit.
 */
export function buildChecklist(
  set: readonly StandardSetItem[],
  candidates: readonly StandardCandidate[],
  thresholds: CoverageThresholds = DEFAULT_THRESHOLDS,
): StandardsChecklist {
  // Best score and matching segments per standard, across every objective's candidates.
  const best = new Map<string, { score: number; segments: Set<number> }>();
  for (const candidate of candidates) {
    if (candidate.score < MIN_REPORTABLE_SCORE) continue;
    const entry = best.get(candidate.caseIdentifierUUID) ?? { score: 0, segments: new Set<number>() };
    entry.score = Math.max(entry.score, candidate.score);
    entry.segments.add(candidate.segmentIndex);
    best.set(candidate.caseIdentifierUUID, entry);
  }

  const structure = new Map<string, string>();
  for (const item of set) {
    if (item.normalizedStatementType !== 'Standard') structure.set(item.statementCode, item.description);
  }

  const clusters = new Map<string, ChecklistCluster>();
  for (const item of set) {
    if (item.normalizedStatementType !== 'Standard') continue;
    const code = clusterCodeOf(item.statementCode);
    let cluster = clusters.get(code);
    if (!cluster) {
      cluster = { code, description: structure.get(code) ?? null, items: [], coveredCount: 0 };
      clusters.set(code, cluster);
    }
    const hit = best.get(item.caseIdentifierUUID);
    const band = hit ? bandFor(hit.score, thresholds) : null;
    cluster.items.push({
      caseIdentifierUUID: item.caseIdentifierUUID,
      statementCode: item.statementCode,
      description: item.description,
      band,
      bestScore: hit ? hit.score : null,
      matchedSegmentIndexes: hit ? [...hit.segments].sort((a, b) => a - b) : [],
    });
    if (band !== null) cluster.coveredCount += 1;
  }

  const ordered = [...clusters.values()].sort((a, b) => a.code.localeCompare(b.code, 'en', { numeric: true }));
  for (const cluster of ordered) {
    cluster.items.sort((a, b) => a.statementCode.localeCompare(b.statementCode, 'en', { numeric: true }));
  }

  return {
    clusters: ordered,
    totalCount: ordered.reduce((n, c) => n + c.items.length, 0),
    coveredCount: ordered.reduce((n, c) => n + c.coveredCount, 0),
  };
}
